import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCopy, RefreshCw, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { CryptoPrivacyBanner } from '../components/CryptoPrivacyBanner';
import { ToolHeader } from '../components/ToolHeader';
import {
  generateTokens,
  TOKEN_LIMITS,
  type TokenEncoding,
} from '../lib/randomToken';

const PRESET_BYTES: ReadonlyArray<{ value: number; label: string }> = [
  { value: 16, label: '16' },
  { value: 32, label: '32' },
  { value: 48, label: '48' },
  { value: 64, label: '64' },
];

const ENCODING_OPTIONS: ReadonlyArray<{ value: TokenEncoding; label: string }> = [
  { value: 'hex', label: 'hex' },
  { value: 'base64', label: 'base64' },
  { value: 'base64url', label: 'base64url' },
];

/**
 * `/tools/crypto/token` — random byte generator for API secrets,
 * refresh tokens, seed values, etc.
 *
 * Defaults are chosen for "I want a strong opaque token, now":
 * 32 bytes (256 bits) in base64url. Both are the de-facto standard
 * for current OAuth / API-key designs.
 */
export default function TokenTool(): JSX.Element {
  const [bytes, setBytes] = useState<number>(32);
  const [encoding, setEncoding] = useState<TokenEncoding>('base64url');
  const [count, setCount] = useState<number>(1);
  const [tokens, setTokens] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const regenerate = useCallback(() => {
    try {
      setTokens(generateTokens(bytes, encoding, count));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setTokens([]);
    }
  }, [bytes, encoding, count]);

  // Auto-fill the page on first paint and whenever the inputs change so
  // the user can compare different settings without clicking refresh.
  useEffect(() => {
    regenerate();
  }, [regenerate]);

  const allCopiedText = useMemo(() => tokens.join('\n'), [tokens]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="token"
        category="crypto"
        description="基于 crypto.getRandomValues 的密码学随机字节，适合做 API secret / refresh token / 测试占位密钥。"
      />

      <CryptoPrivacyBanner />

      <section className="card-pad flex flex-col gap-3">
        <BytesRow value={bytes} onChange={setBytes} />
        <EncodingRow value={encoding} onChange={setEncoding} />
        <CountRow value={count} onChange={setCount} />

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1"
            onClick={regenerate}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            重新生成
          </button>
          {tokens.length > 0 && <CopyAllButton value={allCopiedText} />}
        </div>

        {error && (
          <p className="inline-flex items-center gap-1 text-sm text-rose-600 dark:text-rose-400">
            <XCircle className="h-3.5 w-3.5" />
            {error}
          </p>
        )}
      </section>

      {tokens.length > 0 && (
        <section className="card-pad flex flex-col gap-1">
          <header className="text-[11px] uppercase tracking-wide text-ink-dim">
            结果（{tokens.length}）
          </header>
          <ul className="flex flex-col divide-y divide-line/40">
            {tokens.map((t, i) => (
              <li
                key={`${i}-${t}`}
                className="flex items-center gap-2 py-1 font-mono text-[12px]"
              >
                <span className="w-7 shrink-0 text-right text-ink-dim">{i + 1}.</span>
                <span className="min-w-0 flex-1 break-all text-ink">{t}</span>
                <CopyButton value={t} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function BytesRow({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}): JSX.Element {
  const [custom, setCustom] = useState<string>('');
  const isCustom = !PRESET_BYTES.some((p) => p.value === value);

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="w-16 text-ink-muted">字节数</span>
      {PRESET_BYTES.map((p) => (
        <button
          key={p.value}
          type="button"
          className={clsx(
            'rounded border px-2 py-0.5 text-xs',
            value === p.value
              ? 'border-accent bg-accent/10 text-accent'
              : 'border-line text-ink-muted hover:border-accent/50',
          )}
          onClick={() => {
            setCustom('');
            onChange(p.value);
          }}
        >
          {p.label}
        </button>
      ))}
      <input
        type="number"
        min={TOKEN_LIMITS.MIN_BYTES}
        max={TOKEN_LIMITS.MAX_BYTES}
        placeholder="自定义"
        className={clsx(
          'input w-24 py-1 text-xs',
          isCustom && 'border-accent',
        )}
        value={isCustom ? custom || String(value) : custom}
        onChange={(e) => {
          setCustom(e.target.value);
          const n = Number(e.target.value);
          if (Number.isFinite(n) && Number.isInteger(n)) onChange(n);
        }}
      />
    </div>
  );
}

function EncodingRow({
  value,
  onChange,
}: {
  value: TokenEncoding;
  onChange: (v: TokenEncoding) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="w-16 text-ink-muted">编码</span>
      {ENCODING_OPTIONS.map((o) => (
        <label
          key={o.value}
          className={clsx(
            'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs cursor-pointer',
            value === o.value
              ? 'border-accent bg-accent/10 text-accent'
              : 'border-line text-ink-muted hover:border-accent/50',
          )}
        >
          <input
            type="radio"
            name="token-encoding"
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className="sr-only"
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}

function CountRow({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="w-16 text-ink-muted">数量</span>
      <input
        type="number"
        min={1}
        max={TOKEN_LIMITS.MAX_COUNT}
        className="input w-24 py-1 text-xs"
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n) && Number.isInteger(n)) onChange(n);
        }}
      />
      <span className="text-[11px] text-ink-dim">≤ {TOKEN_LIMITS.MAX_COUNT}</span>
    </div>
  );
}

function CopyButton({ value }: { value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded p-1 text-ink-dim hover:bg-bg-soft hover:text-accent"
      title="复制"
    >
      {done ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
      ) : (
        <ClipboardCopy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

function CopyAllButton({ value }: { value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded border border-line px-2 py-1 text-xs text-ink-muted hover:border-accent/50 hover:text-accent"
    >
      {done ? '已复制全部' : '全部复制'}
    </button>
  );
}
